import { useCart } from '@/components/Cart';
import { ActionIcon, Alert, Box, Button, Group, LoadingOverlay, Paper, Stack, Table, Text, Title, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconAlertCircle, IconShoppingCart, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

const Cart: React.FC = () => {
  useEffect(() => {
    document.title = "Shopping Cart - Guard Dog Real Estate Protection Service";
  }, []);

  const { items, total, loading, removeItem, clearCart } = useCart();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get('cancelled') === 'true';

  const handleRemoveItem = (cartItemId: number, documentNumber: string) => {
    modals.openConfirmModal({
      title: 'Remove Item',
      centered: true,
      children: <Text>Are you sure you want to remove document {documentNumber} from your cart?</Text>,
      labels: { confirm: 'Remove', cancel: 'Cancel' },
      confirmProps: { color: 'pq-red' },
      onConfirm: () => removeItem(cartItemId)
    });
  };

  const handleClearCart = () => {
    modals.openConfirmModal({
      title: 'Clear Cart',
      centered: true,
      children: <Text>Are you sure you want to remove all items from your cart?</Text>,
      labels: { confirm: 'Clear Cart', cancel: 'Cancel' },
      confirmProps: { color: 'pq-red' },
      onConfirm: () => clearCart()
    });
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  const formatPrice = (price: number) => {
    return `$${price.toFixed(2)}`;
  };

  return (
    <Box pos="relative">
      <LoadingOverlay visible={loading} overlayProps={{ blur: 2, radius: 'sm' }} loaderProps={{ size: 'xl' }} />

      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <IconShoppingCart size={28} color="var(--mantine-color-pq-red-6)" />
            <Title order={2}>Shopping Cart</Title>
          </Group>
          {items.length > 0 && (
            <Button variant="outline" color="pq-red" onClick={handleClearCart}>
              Clear Cart
            </Button>
          )}
        </Group>

        {cancelled && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Payment Cancelled"
            color="yellow"
            withCloseButton
            onClose={() => navigate('/cart', { replace: true })}
          >
            Your payment was cancelled. Your items are still in your cart.
          </Alert>
        )}

        {items.length === 0 ? (
          <Paper p="xl" withBorder>
            <Stack align="center" gap="md">
              <IconShoppingCart size={64} color="var(--mantine-color-gray-5)" />
              <Text c="dimmed" size="lg">Your cart is empty</Text>
              <Text c="dimmed" size="sm">Add documents from your dashboard alerts to get started.</Text>
              <Button color="pq-red" onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            </Stack>
          </Paper>
        ) : (
          <>
            <Paper p="md" withBorder>
              <Box style={{ overflowX: 'auto' }}>
                <Table stickyHeader style={{ minWidth: 700 }}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Document</Table.Th>
                      <Table.Th>APN</Table.Th>
                      <Table.Th>Property Address</Table.Th>
                      <Table.Th>County</Table.Th>
                      <Table.Th>Date</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>Price</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}></Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {items.map((item) => (
                      <Table.Tr key={item.id}>
                        <Table.Td>
                          <Text fw={500}>{item.documentNumber}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{item.apn || 'N/A'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" style={{ maxWidth: 200 }} lineClamp={2}>
                            {item.propertyAddress || 'N/A'}
                          </Text>
                        </Table.Td>
                        <Table.Td>{item.county}</Table.Td>
                        <Table.Td>
                          {item.documentDate ? dayjs(item.documentDate).format('MM/DD/YYYY') : '-'}
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          {formatPrice(item.price)}
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4} justify="flex-end">
                            <Tooltip label="Remove from cart">
                              <ActionIcon
                                onClick={() => handleRemoveItem(item.id, item.documentNumber)}
                                radius="xl"
                                size="sm"
                                color="pq-red"
                                aria-label='Remove from cart'
                                variant="light"
                              >
                                <IconTrash size={15} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>
            </Paper>

            <Paper p="md" withBorder>
              <Group justify="space-between" align="center">
                <Stack gap={4}>
                  <Text size="sm" c="dimmed">
                    {items.length} {items.length === 1 ? 'item' : 'items'}
                  </Text>
                  <Group gap="xs">
                    <Text size="xl" fw={700}>Total:</Text>
                    <Text size="xl" fw={700} c="pq-red">{formatPrice(total)}</Text>
                  </Group>
                </Stack>
                <Button size="lg" color="pq-red" onClick={handleCheckout}>
                  Proceed to Checkout
                </Button>
              </Group>
            </Paper>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default Cart;
